import * as api from './api';
import { supabase } from '../supabaseClient';
import { getOrgId } from './orgStore';

export const fetchPurchases = async (searchTerm = '') => {
    const orgId = getOrgId();
    if (!orgId) throw new Error('No org selected (org_id is null).');

    const [purchaseRes, supplierRes, itemRes, taxRes, setupRes, setup2Res] =
    await Promise.all([
      supabase
        .from('purchase_order')
        .select('*, supplier:supplier_id(supplier_name)')
        .eq('org_id', orgId)
        .order('purchase_order_date', { ascending: false }),
      supabase.from('supplier').select('*').eq('org_id', orgId).order('supplier_id', { ascending: true }),
      supabase.from('item_with_stock').select('*').eq('org_id', orgId).order('item_id', { ascending: true }),
      supabase.from('tax').select('*').or(`org_id.eq.${orgId},is_global.eq.true`),
      supabase.from('setup').select('*').eq('org_id', orgId),
      supabase.from('setup2').select('*').eq('org_id', orgId),
    ]);

    if (purchaseRes.error) throw purchaseRes.error;
    if (supplierRes.error) throw supplierRes.error;
    if (itemRes.error) throw itemRes.error;
    if (taxRes.error) throw taxRes.error;
    if (setupRes.error) throw setupRes.error;
    if (setup2Res.error) throw setup2Res.error;

    let purchases = (purchaseRes.data || []).map((purchase) => ({
      ...purchase,
    }));

    // Client-side search on purchase number or supplier name
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      purchases = purchases.filter(
        (p) =>
          p.purchase_number?.toString().toLowerCase().includes(term) ||
          p.supplier?.supplier_name?.toLowerCase().includes(term)
      );
    }

    return {
        purchases,
        suppliers: supplierRes.data || [],
        items: itemRes.data || [],
        taxes: taxRes.data || [],
        setup: setupRes.data || [],
        setup2: setup2Res.data || [],
    };  
};

/**
 * Fetch line items for a specific purchase (used when editing).
 */
export const fetchPurchaseLines = async (purchaseId) => {
    const { data, error } = await supabase
      .from('purchase_order_line_items')
      .select('*')
      .eq('purchase_order_id', purchaseId)
      .order('line_number', { ascending: true });
    if (error) throw error;
    return data || [];
  };


/**
 * Save a purchase (create or update).
 */
export const savePurchase = async ({ header, lines }, existingPurchaseId = null) => {
    let purchaseId = existingPurchaseId;

    if (existingPurchaseId) {
        // --- UPDATE ---
        const { error } = await supabase
            .from('purchase_order')
            .update(header)
            .eq('purchase_order_id', existingPurchaseId);
        if (error) throw error;
    } else {
        // --- CREATE ---
        const { purchase_order_id: _strip, ...insertPayload } = header;
        const { data, error } = await supabase
            .from('purchase_order')
            .insert([{ ...insertPayload, org_id: getOrgId() }])
            .select()
            .single();
        if (error) throw error;
        purchaseId = data.purchase_order_id;
    }
    
    // --- SAVE LINE ITEMS (full-replace strategy) ---
    await supabase.from('purchase_order_line_items').delete().eq('purchase_order_id', purchaseId);

    const orgId = getOrgId();
    const linesToInsert = lines
        .map((line, idx) => ({
            purchase_order_id: purchaseId,
            org_id: orgId,
            line_number: idx + 1,
            item_id: line.item_id || null,
            item_description: line.item_description,
            qty: line.quantity,
            unit_price: line.unit_price,
            cost_excl: line.unit_price,
            line_total_inclusive_tax: line.total_price,
        }))
        .filter((l) => l.item_description || l.unit_price > 0);

    if (linesToInsert.length > 0) {
        const { error: insertError } = await supabase
            .from('purchase_order_line_items')
            .insert(linesToInsert);
        if (insertError) throw insertError;
    }

    return purchaseId;
};

/**
 * Delete a purchase and its line items.
 */
export const deletePurchase = async (purchaseId) => {
    await supabase.from('purchase_order_line_items').delete().eq('purchase_order_id', purchaseId);
    await api.deleteRecord('purchase_order', 'purchase_order_id', purchaseId);
};